import { defineStore } from "pinia";
import { useStoryTypeRenderConfigStore } from "%/dynamic-component-editor/stores/story-type-render-config-store";

const pendingConfigRequests = new Map();

export const useCompletionAssignmentStore = defineStore("completion-assignment-store", {
  state: () => ({
    configByPublication: {},
    assignmentsByPublication: {},
    subVariantsByPublication: {},
    twoStateModeByPublication: {},
    storyTypeCardsByPublication: {},
  }),
  getters: {
    preloadedConfig() {
      return useStoryTypeRenderConfigStore().renderConfig;
    },
  },
  actions: {
    // Render surfaces get the config stamped into the page; every other surface
    // reads it in ONE request. Both land in the same maps, so the write paths
    // below patch state without caring which way it arrived.
    ensureStoryTypeConfig(publicationId) {
      if (!publicationId || publicationId in this.configByPublication) {
        return Promise.resolve();
      }
      if (this.preloadedConfig) {
        this.applyStoryTypeConfig(publicationId, this.preloadedConfig);
        return Promise.resolve();
      }
      if (pendingConfigRequests.has(publicationId)) {
        return pendingConfigRequests.get(publicationId);
      }
      const request = this.apiGet(
        `/dynamic-component/${publicationId}/story-type-config`,
        {},
        config => {
          this.applyStoryTypeConfig(publicationId, config && typeof config === "object" ? config : {});
        },
        () => {
          this.applyStoryTypeConfig(publicationId, {});
        },
      ).finally(() => {
        pendingConfigRequests.delete(publicationId);
      });
      pendingConfigRequests.set(publicationId, request);

      return request;
    },
    applyStoryTypeConfig(publicationId, config) {
      this.configByPublication[publicationId] = config;
      this.assignmentsByPublication[publicationId] = config.completionComponentAssignments ?? {};
      this.storyTypeCardsByPublication[publicationId] = config.cardAssignments ?? {};
      this.subVariantsByPublication[publicationId] = config.completionSubVariantAssignments ?? {};
      this.twoStateModeByPublication[publicationId] = Boolean(config.completionTwoStateMode);
    },
    storyTypeConfig(publicationId) {
      return this.configByPublication[publicationId] ?? null;
    },
    ensureAssignments(publicationId) {
      return this.ensureStoryTypeConfig(publicationId);
    },
    ensureStoryTypeCardAssignments(publicationId) {
      return this.ensureStoryTypeConfig(publicationId);
    },
    storyTypeCardAssignments(publicationId) {
      return this.storyTypeCardsByPublication[publicationId] ?? {};
    },
    refreshStoryTypeConfig(publicationId) {
      if (!publicationId) {
        return;
      }
      delete this.configByPublication[publicationId];
      pendingConfigRequests.delete(publicationId);

      return this.ensureStoryTypeConfig(publicationId);
    },
    applyStoryTypeCardAssignment(publicationId, storyType, dynamicComponentId) {
      this.storyTypeCardsByPublication[publicationId] = {
        ...this.storyTypeCardAssignments(publicationId),
        [storyType]: dynamicComponentId,
      };
    },
    applyTwoStateMode(publicationId, enabled) {
      this.twoStateModeByPublication[publicationId] = enabled;
    },
    // XXX: Feature probe ONLY -- "is completion tracking configured for this
    // XXX: destination at all". Deliberately separate from resolveComponentId:
    // XXX: card BINDING is per-element and never falls back to a slot, so a
    // XXX: consumer asking "is the feature on" must not do it by resolving a
    // XXX: card and comparing to 0. That shared call is what made the two
    // XXX: concerns look like one.
    isTrackingConfigured(publicationId) {
      const assignments = this.assignmentsByPublication[publicationId];
      if (!assignments) {
        return false;
      }
      return Object.values(assignments).some(id => Number(id) > 0);
    },
    // XXX: The publication-level slot map. It SEEDS an element's card choice at
    // XXX: drop time and is read by the assign-components picker. It is NOT a
    // XXX: render-time fallback -- an element with no per-element card is a
    // XXX: broken state and must say so.
    resolveComponentId(publicationId, slotKey) {
      const assignments = this.assignmentsByPublication[publicationId];
      if (!assignments) {
        return 0;
      }
      return Number(assignments[slotKey]) || 0;
    },
    ensureSubVariantAssignments(publicationId) {
      return this.ensureStoryTypeConfig(publicationId);
    },
    resolveSubVariantId(publicationId, baseCardDynamicComponentId, completionStatus) {
      const assignments = this.subVariantsByPublication[publicationId];
      if (!assignments) {
        return 0;
      }
      return Number(assignments[`${baseCardDynamicComponentId}:${completionStatus}`]) || 0;
    },
    ensureTwoStateMode(publicationId) {
      return this.ensureStoryTypeConfig(publicationId);
    },
    isTwoStateMode(publicationId) {
      return Boolean(this.twoStateModeByPublication[publicationId]);
    },
    normalizeStatus(publicationId, completionStatus) {
      if (this.isTwoStateMode(publicationId) && completionStatus === "started") {
        return "not_started";
      }
      return completionStatus;
    },
  },
});
